import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { User, Building2, Package, X, Download, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { QrCodeImage } from "./QrCodeImage";
import { maxPlatform } from "../services/maxPlatform";
import { useAppStore } from "../store/useAppStore";
import { downloadQrPng } from "../utils/codeImages";

export type ShareQrCardItem = {
  id: string;
  title: string;
  subtitle?: string;
  qrValue: string;
  kind: "self" | "booth" | "colleague" | "product";
};

async function shareOrCopy(text: string, url: string): Promise<"shared" | "copied" | "fail"> {
  const ok = await maxPlatform.shareText(text, url);
  if (ok) return "shared";
  try {
    await navigator.clipboard.writeText(url || text);
    return "copied";
  } catch {
    return "fail";
  }
}

function kindIcon(kind: ShareQrCardItem["kind"]) {
  if (kind === "booth") return Building2;
  if (kind === "product") return Package;
  return User;
}

export function ShareQrCarousel({ items }: { items: ShareQrCardItem[] }) {
  const { t } = useTranslation();
  const { setNavbarVisible } = useAppStore();
  const [preview, setPreview] = useState<ShareQrCardItem | null>(null);
  const [downloading, setDownloading] = useState(false);

  const visible = useMemo(() => items.filter((i) => i.qrValue.trim()), [items]);

  useEffect(() => {
    if (!preview) return;
    setNavbarVisible(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPreview(null);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      setNavbarVisible(true);
    };
  }, [preview, setNavbarVisible]);

  if (visible.length === 0) return null;

  const openPreview = (item: ShareQrCardItem) => {
    maxPlatform.hapticFeedback("light");
    setPreview(item);
  };

  const closePreview = () => setPreview(null);

  const handleDownload = async () => {
    if (!preview?.qrValue) return;
    setDownloading(true);
    try {
      await downloadQrPng(preview.qrValue, `${preview.title}-${preview.kind}`);
      maxPlatform.hapticFeedback("medium");
      toast.success(t("common.downloaded", { defaultValue: "QR сохранён" }));
    } catch {
      toast.error(t("visitor.loading_error"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="-mx-1">
        <div
          className="flex gap-3 overflow-x-auto px-1 pb-1 snap-x snap-mandatory"
          style={{ scrollbarWidth: "none" }}
        >
          {visible.map((item) => {
            const Icon = kindIcon(item.kind);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => openPreview(item)}
                className="snap-start flex-shrink-0 w-[188px] rounded-2xl bg-gray-50 border border-gray-100 p-3 flex flex-col text-left active:scale-[0.98] transition-transform"
                aria-label={t("profile.qr_enlarge")}
              >
                <div className="flex items-center gap-2 mb-2 min-w-0">
                  <div className="w-7 h-7 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center flex-shrink-0">
                    <Icon size={14} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[12px] text-gray-900 truncate font-medium">{item.title}</p>
                    {item.subtitle ? (
                      <p className="text-[10px] text-gray-400 truncate">{item.subtitle}</p>
                    ) : null}
                  </div>
                </div>
                <div className="flex justify-center my-0.5">
                  <QrCodeImage value={item.qrValue} size={132} className="!rounded-xl" />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {typeof document !== "undefined"
        ? createPortal(
            <AnimatePresence>
              {preview ? (
                <motion.div
                  key="qr-preview"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-black/75 px-5"
                  role="dialog"
                  aria-modal="true"
                  aria-label={preview.title}
                  onClick={closePreview}
                >
                  <button
                    type="button"
                    onClick={closePreview}
                    aria-label={t("common.close")}
                    className="absolute top-[max(1rem,env(safe-area-inset-top))] right-4 w-11 h-11 rounded-full bg-white/95 text-gray-800 flex items-center justify-center shadow-lg active:scale-95"
                  >
                    <X size={22} />
                  </button>

                  <motion.div
                    initial={{ scale: 0.92, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.96, opacity: 0 }}
                    transition={{ type: "spring", damping: 22, stiffness: 280 }}
                    className="w-full max-w-sm rounded-3xl bg-white p-5 shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <p className="text-center text-[16px] text-gray-900 font-medium truncate">
                      {preview.title}
                    </p>
                    {preview.subtitle ? (
                      <p className="text-center text-[13px] text-gray-500 mt-0.5 truncate">
                        {preview.subtitle}
                      </p>
                    ) : null}
                    <div className="flex justify-center mt-4">
                      <QrCodeImage value={preview.qrValue} size={260} className="!rounded-2xl" />
                    </div>
                    <p className="mt-3 text-center text-[12px] text-gray-400">
                      {t("profile.qr_scan_hint")}
                    </p>
                    <button
                      type="button"
                      onClick={() => void handleDownload()}
                      disabled={downloading}
                      className="mt-4 w-full h-11 rounded-xl bg-violet-500 text-white text-[14px] font-medium flex items-center justify-center gap-2 active:bg-violet-600 disabled:opacity-60"
                    >
                      {downloading ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Download size={16} />
                      )}
                      {t("common.download", { defaultValue: "Скачать" })}
                    </button>
                    <button
                      type="button"
                      onClick={closePreview}
                      className="mt-2 w-full h-11 rounded-xl text-gray-500 text-[14px] font-medium active:bg-gray-50"
                    >
                      {t("common.close")}
                    </button>
                  </motion.div>
                </motion.div>
              ) : null}
            </AnimatePresence>,
            document.body,
          )
        : null}
    </>
  );
}

export { shareOrCopy };
